import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
    arrayOfStringsToStringWithSeparator,
    prettifyCardNumber,
    prettifyDate
} from '../../../../utils/string';
import { oneLineAddress } from '../../../../utils/address';
import {
    clearSearchTransactions,
    searchTransactions
} from '../../../Transactions/transactionsActions';
import {
    getIsSearchTransactionsLoaded,
    getIsSearchTransactionsLoading,
    getSearchTransactions
} from '../../../Transactions/transactionsEntity';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
    get,
    isEqual
} from 'lodash';
import {
    clearSearchAll,
    getTransactionLink,
    getCustomerLink,
    searchAll
} from '../../searchActions';
import {
    getIsSearchLoaded,
    getIsSearchLoading,
    getSearchResults
} from '../../searchEntity';
import { getInputValue } from '../../searchViewState';
import Section from './Section';
import NoResults from './NoResults';
import classNames from 'classnames';
import { EmvioLoadingSpinner as LoadingSpinner } from '@nexio/emvio-util-ui';
import {
    customerShape,
    transactionShape
} from '../../../../utils/propTypes';
import { getSelectedMerchantIdsString } from '../../../MerchantSelector/merchantSelectorViewState';
import { historyShape } from '../../../../utils/propTypes';
import './Results.scss';
import {
    cardTypesMapper,
    doesPropExistAndHasItChanged,
    prettifyMoney
} from '@nexio/emvio-util-app';

export function hasSuggestions(suggestions = [], isMultiSection) {
    if (isMultiSection) {
        return !!Object.keys(suggestions).some((key) => (
            suggestions[key].length
        ));
    }

    return !!suggestions.length;
}

export function getShouldDisplayNoResults(suggestions, isLoading, isLoaded, inputValue = '', lastFetchedInputValue, isMultiSection) {
    const hasSomeSuggestions = hasSuggestions(suggestions, isMultiSection);

    return (!hasSomeSuggestions && !isLoading && isLoaded
        && inputValue.trim().length && inputValue === lastFetchedInputValue);
}

export class Results extends Component {
    static propTypes = {
        customers: PropTypes.arrayOf(
            customerShape
        ),
        history: historyShape,
        inputValue: PropTypes.string,
        isCustomersLoaded: PropTypes.bool,
        isCustomersLoading: PropTypes.bool,
        isSearchAllLoaded: PropTypes.bool,
        isSearchAllLoading: PropTypes.bool,
        isTransactionsLoaded: PropTypes.bool,
        isTransactionsLoading: PropTypes.bool,
        match: PropTypes.shape({
            params: PropTypes.shape({
                filter: PropTypes.string,
                query: PropTypes.string
            })
        }),
        onClearCustomers: PropTypes.func,
        onClearSearchAll: PropTypes.func,
        onClearTransactions: PropTypes.func,
        onGetCustomers: PropTypes.func,
        onGetTransactions: PropTypes.func,
        onSearchAll: PropTypes.func,
        onSelectCustomer: PropTypes.func,
        onSelectTransaction: PropTypes.func,
        searchResults: PropTypes.shape({
            customers: PropTypes.arrayOf(customerShape),
            transactions: PropTypes.arrayOf(transactionShape)
        }),
        selectedMerchantIds: PropTypes.string,
        transactions: PropTypes.arrayOf(
            transactionShape
        )
    };

    static defaultProps = {
        customers: [],
        searchResults: {},
        transactions: []
    };

    constructor(props) {
        super(props);

        this.renderSections = this.renderSections.bind(this);
        this.customerPropGenerator = this.customerPropGenerator.bind(this);
        this.transactionPropGenerator = this.transactionPropGenerator.bind(this);
        this.handleFetch = this.handleFetch.bind(this);
        this.handleClearSuggestions = this.handleClearSuggestions.bind(this);
        this.filterTypeToPropMapper = {
            all: {
                clear: 'onClearSearchAll',
                fetch: 'onSearchAll',
                isLoaded: 'isSearchAllLoaded',
                isLoading: 'isSearchAllLoading',
                isMultiSection: true,
                suggestions: 'searchResults'
            },
            transactions: {
                clear: 'onClearTransactions',
                fetch: 'onGetTransactions',
                isLoaded: 'isTransactionsLoaded',
                isLoading: 'isTransactionsLoading',
                propGenerator: this.transactionPropGenerator,
                sectionTitle: 'Transactions',
                suggestions: 'transactions'
            }
        };

        this.state = {
            animateDropDown: false,
            fetchedInputValue: ''
        };
    }

    handleFetch() {
        const query = this.props.match.params.query;

        if (query) {
            this.setState({
                fetchedInputValue: query
            });

            this.props[this.filterTypeToPropMapper[this.props.match.params.filter].fetch](query, this.props.selectedMerchantIds);
        }
    }

    handleClearSuggestions() {
        this.setState({
            fetchedInputValue: '',
            shouldDisplayNoResults: false
        });

        Object.keys(this.filterTypeToPropMapper).forEach((filterType) => {
            this.props[this.filterTypeToPropMapper[filterType].clear]();
        });
    }

    componentDidMount() {
        this.handleFetch();
    }

    componentDidUpdate(previousProps) {
        if (!isEqual(this.props.match.params.query, previousProps.match.params.query)
            || !isEqual(this.props.match.params.filter, previousProps.match.params.filter)) {
            this.handleClearSuggestions();
            if (this.props.match.params.query) {
                this.handleFetch();
            }
        }

        const propMapper = this.filterTypeToPropMapper[this.props.match.params.filter];

        const suggestions = this.props[propMapper.suggestions];
        const previousSuggestions = previousProps[propMapper.suggestions];
        const isLoading = this.props[propMapper.isLoading];
        const isLoaded = this.props[propMapper.isLoaded];
        const isMultiSection = propMapper.isMultiSection;

        const shouldDisplayNoResults = getShouldDisplayNoResults(
            suggestions,
            isLoading,
            isLoaded,
            this.props.inputValue,
            this.state.fetchedInputValue,
            isMultiSection
        );

        if (!isEqual(shouldDisplayNoResults, this.state.shouldDisplayNoResults)) {
            this.setState({
                shouldDisplayNoResults
            });
        }

        if (!isEqual(suggestions, previousSuggestions)
            || !isEqual(shouldDisplayNoResults, this.state.shouldDisplayNoResults)) {

            if (shouldDisplayNoResults || hasSuggestions(suggestions, isMultiSection)) {
                this.setState({
                    animateDropDown: true
                });
            } else {
                this.setState({
                    animateDropDown: false
                });
            }
        }

        if (doesPropExistAndHasItChanged(previousProps, this.props, 'selectedMerchantIds')) {
            this.handleFetch();
        }
    }

    customerPropGenerator(customer = {}) {
        const address = oneLineAddress(customer);
        const addressAndPhone = arrayOfStringsToStringWithSeparator([address, customer.phone]);
        const companyNameAndName = arrayOfStringsToStringWithSeparator([
            customer.company,
            arrayOfStringsToStringWithSeparator([
                customer.firstName,
                customer.lastName
            ])], ', ');

        return {
            firstRowText: companyNameAndName,
            resultLink: getCustomerLink(customer),
            secondRowText: addressAndPhone
        };
    }

    transactionPropGenerator(transaction = {}) {
        const transactionHighlightProps = arrayOfStringsToStringWithSeparator([
            prettifyDate(transaction.transactionDate, { returnDateOnly: true }),
            get(cardTypesMapper, transaction.cardType, '').toUpperCase(),
            prettifyCardNumber(transaction.cardNumber),
            prettifyMoney(transaction.amount),
            get(transaction, 'id'),
            get(transaction, 'plugin.orderNumber')
        ]);

        const nameAndCompanyName = arrayOfStringsToStringWithSeparator([
            get(transaction, 'customer.firstName'),
            get(transaction, 'customer.lastName'),
            get(transaction, 'customer.company')
        ], ', ');
        const refNumber = get(transaction, 'gateway.refNumber');
        const customer = get(transaction, 'customer');

        if (customer) {
            const address = oneLineAddress(customer);
            const addressAndPhone = arrayOfStringsToStringWithSeparator([address, customer.phone]);

            return {
                firstRowExtraSpacing: true,
                resultLink: getTransactionLink(transaction),
                textRows: [
                    transactionHighlightProps,
                    nameAndCompanyName,
                    addressAndPhone,
                    refNumber
                ]
            };
        } else {
            return {
                firstRowExtraSpacing: true,
                resultLink: getTransactionLink(transaction),
                textRows: [
                    transactionHighlightProps,
                    nameAndCompanyName,
                    refNumber
                ]
            };
        }

    }

    renderSections() {
        const propMapper = this.filterTypeToPropMapper[this.props.match.params.filter];
        if (propMapper.isMultiSection) {
            if (!hasSuggestions(this.props[propMapper.suggestions], propMapper.isMultiSection)) {
                return null;
            }

            return Object.keys(this.filterTypeToPropMapper)
                .filter((filterType) => (!this.filterTypeToPropMapper[filterType].isMultiSection))
                .map((filterType) => {
                    const filterTypePropMapper = this.filterTypeToPropMapper[filterType];

                    return (<Section
                        inputValue={this.state.fetchedInputValue}
                        key={filterType}
                        onSelectResult={this.handleClearSuggestions}
                        propGenerator={filterTypePropMapper.propGenerator}
                        results={this.props[propMapper.suggestions][filterType]}
                        sectionTitle={filterTypePropMapper.sectionTitle}
                    />);
                });
        }

        return <Section
            inputValue={this.state.fetchedInputValue}
            propGenerator={propMapper.propGenerator}
            results={this.props[propMapper.suggestions]}
            sectionTitle={propMapper.sectionTitle}
        />;
    }

    render() {
        const propMapper = this.filterTypeToPropMapper[this.props.match.params.filter];
        const isLoading = this.props[propMapper.isLoading];
        const containerClassNames = classNames('emvio-search-results', {
            'animate-drop-down': this.state.animateDropDown,
            'search-loading': isLoading,
            'search-results-none': this.state.shouldDisplayNoResults
        });

        return (
            <div className={containerClassNames}>
                <div className="search-results">
                    {isLoading ?
                        <div className="search-spinner">
                            <LoadingSpinner />
                        </div> : null
                    }
                    {this.state.shouldDisplayNoResults
                        ? <NoResults inputValue={this.state.fetchedInputValue} />
                        : this.renderSections()
                    }
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        inputValue: getInputValue(state),
        isSearchAllLoaded: getIsSearchLoaded(state),
        isSearchAllLoading: getIsSearchLoading(state),
        isTransactionsLoaded: getIsSearchTransactionsLoaded(state),
        isTransactionsLoading: getIsSearchTransactionsLoading(state),
        searchResults: getSearchResults(state),
        selectedMerchantIds: getSelectedMerchantIdsString(state),
        transactions: getSearchTransactions(state)
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onClearSearchAll: () => dispatch(clearSearchAll()),
        onClearTransactions: () => dispatch(clearSearchTransactions()),
        onGetTransactions: (query, merchantIds) => dispatch(searchTransactions(query, merchantIds)),
        onSearchAll: (query, merchantIds) => dispatch(searchAll(query, merchantIds))
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Results));
